<script setup>
import { useCalendarStore } from '@/stores/CalendarStore'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { computed, defineProps, onMounted } from 'vue'
import { useBreadcrumbStore } from '@/stores/BreadcrumbStore'
import { CALENDAR_VIEW_SETTING_KEY, setAppSetting } from '@/composables/useAppSettingsCookie'
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/solid";

dayjs.extend(advancedFormat)

const props = defineProps({
  view: { type: String, required: true },
})
const emit = defineEmits(['navigate-today'])

let calendarStore = useCalendarStore()
const route = useRoute()
const router = useRouter()
const breadcrumbStore = useBreadcrumbStore()
const activeView = computed(() => props.view)
const selectedMonthKey = computed(() => dayjs(calendarStore.getDate).format('YYYY-MM'))

const hasRevenueAccess = computed(() =>
  calendarStore.getEntries.some(entry =>
    Object.prototype.hasOwnProperty.call(entry ?? {}, 'daily_revenue_subtotals')
      || Object.prototype.hasOwnProperty.call(entry ?? {}, 'daily_invoice_subtotals'),
  ),
)

const monthRevenueSubtotal = computed(() => {
  return calendarStore.getEntries.reduce((total, entry) => {
    const subtotals = entry?.daily_revenue_subtotals ?? entry?.daily_invoice_subtotals
    if (!subtotals || typeof subtotals !== 'object') {
      return total
    }

    return total + Object.entries(subtotals).reduce((entryTotal, [date, amount]) => {
      if (!date.startsWith(selectedMonthKey.value)) {
        return entryTotal
      }

      return entryTotal + Number(amount ?? 0)
    }, 0)
  }, 0)
})

const formatRevenueSubtotal = amount => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
  maximumFractionDigits: amount % 100 === 0 ? 0 : 2,
}).format(amount / 100)

onMounted(async () => {
  calendarStore.updateDateFromRoute(route)
  update()
})

const update = () => {
  setAppSetting(CALENDAR_VIEW_SETTING_KEY, props.view === 'day' ? 'day' : 'month')

  const query = {
    date: dayjs(calendarStore.getDate).format('YYYY-MM-DD'),
  }
  if (props.view === 'day') {
    query.view = 'day'
  } else {
    delete query.view
  }

  router.replace({ path: '/calendar', query })
  breadcrumbStore.setValue(
    props.view === 'month'
      ? dayjs(calendarStore.getDate).format('MMMM YYYY')
      : dayjs(calendarStore.getDate).format('ddd, Do MMMM YYYY'),
  )
}

const changeMonth = amount => {
  calendarStore.changeMonth(amount)
  update()
}

const changeDay = amount => {
  calendarStore.changeDay(amount)
  update()
}

const goToToday = () => {
  const today = dayjs()

  if (!dayjs(calendarStore.getDate).isSame(today, 'day')) {
    calendarStore.setDate(today)
  }

  update()
  emit('navigate-today', today.format('YYYY-MM-DD'))
}

const getRelativeDayLabel = targetDate => {
  const target = dayjs(targetDate)
  const today = dayjs()

  if (target.isSame(today, 'day')) {
    return 'Today'
  }

  if (target.isSame(today.add(1, 'day'), 'day')) {
    return 'Tomorrow'
  }

  if (target.isSame(today.subtract(1, 'day'), 'day')) {
    return 'Yesterday'
  }

  return null
}

const previousDayLabel = computed(() => {
  const target = dayjs(calendarStore.getDate).subtract(1, 'day')
  return getRelativeDayLabel(target) ?? 'Prev'
})

const nextDayLabel = computed(() => {
  const target = dayjs(calendarStore.getDate).add(1, 'day')
  return getRelativeDayLabel(target) ?? 'Next'
})

const setView = view => {
  const query = {
    ...route.query,
    date: dayjs(calendarStore.getDate).format('YYYY-MM-DD'),
  }
  const normalizedView = view === 'day' ? 'day' : 'month'

  if (normalizedView === 'day') {
    query.view = 'day'
  } else {
    delete query.view
  }

  setAppSetting(CALENDAR_VIEW_SETTING_KEY, normalizedView)
  router.replace({ path: '/calendar', query })
}
</script>
<template>
  <div class="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
    <div class="flex-1">
      <div class="flex flex-wrap items-center gap-4">
        <router-link
          :to="{ name: 'reference-create' }"
          class="accent rounded text-xl px-2"
        >
          +
        </router-link>
        <div v-if="view === 'month'" class="flex min-w-0 flex-wrap items-baseline gap-3">
          <h1 class="text-xl sm:text-2xl">
            {{ dayjs(calendarStore.getDate).format('MMMM YYYY') }}
          </h1>
          <span
            v-if="hasRevenueAccess"
            title="Expected revenue: invoiced amounts excluding VAT plus booked 365 values for this month"
            class="text-sm font-semibold text-emerald-700 dark:text-emerald-300 sm:text-base"
          >
            {{ formatRevenueSubtotal(monthRevenueSubtotal) }}
          </span>
        </div>
        <h1 class="text-xl sm:text-2xl" v-else>
          {{ dayjs(calendarStore.getDate).format('ddd,  Do MMMM YYYY') }}
        </h1>
      </div>
    </div>

    <div class="flex w-full flex-wrap items-center gap-2 md:w-auto md:justify-end">
      <div class="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 p-1 dark:border-slate-700 dark:bg-slate-800">
        <button
          type="button"
          class="rounded-full px-3 py-1.5 text-xs font-semibold transition sm:px-4 sm:text-sm"
          :class="activeView === 'month'
            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white'
            : 'text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white'"
          @click="setView('month')"
        >
          Month
        </button>
        <button
          type="button"
          class="rounded-full px-3 py-1.5 text-xs font-semibold transition sm:px-4 sm:text-sm"
          :class="activeView === 'day'
            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white'
            : 'text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white'"
          @click="setView('day')"
        >
          Day
        </button>
      </div>

      <div v-show="props.view == 'month'" class="flex flex-wrap gap-2">
        <button
          class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100 sm:px-4 sm:text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          @click.prevent="changeMonth(-1)"
        >
          <ChevronLeftIcon class="h-4 w-4" />
          Prev
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-full border border-cyan-600 bg-cyan-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:border-cyan-500 hover:bg-cyan-500 sm:px-4 sm:text-sm dark:border-cyan-400 dark:bg-cyan-400 dark:text-slate-900 dark:hover:border-cyan-300 dark:hover:bg-cyan-300"
          @click.prevent="goToToday"
        >
          Today
        </button>
        <button class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 sm:px-4 sm:text-sm dark:border-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white" @click.prevent="changeMonth(1)">
          Next
          <ChevronRightIcon class="h-4 w-4" />
        </button>
      </div>

      <div v-show="view == 'day'" class="flex flex-wrap gap-2">
        <button
          class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100 sm:px-4 sm:text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          @click.prevent="changeDay(-1)"
        >
          <ChevronLeftIcon class="h-4 w-4" />
          {{ previousDayLabel }}
        </button>
        <button
          class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 sm:px-4 sm:text-sm dark:border-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
          @click.prevent="changeDay(1)"
        >
          {{ nextDayLabel }}
          <ChevronRightIcon class="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
</template>
